'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, UserRound, Users } from 'lucide-react';
import { ProfilePhotoImg } from '@/components/investigador/ProfilePhotoImg';
import { useIsMobile } from '@/hooks/useIsMobile';
import { HomeSectionTitle } from './HomeSectionTitle';
import { Reveal } from './Reveal';
import { TipoInvestigadorBadge } from './investigadores/TipoInvestigadorBadge';
import { nombreUnidad } from '@/lib/unidad-display';
import type { InvestigatorRecord } from '@/data/researchCatalog';

/** Cuántos perfiles se ven a la vez. En escritorio coincide con el directorio a
 *  zoom ~92%; en celular bajan a 4 para que la sección entre completa. */
const POR_GRUPO_ESCRITORIO = 5;
const POR_GRUPO_CELULAR = 4;
/** Cada cuánto rota, en ms. */
const INTERVALO = 6000;

const numberFormatter = new Intl.NumberFormat('es-CL');

function agrupar(investigators: InvestigatorRecord[], porGrupo: number) {
	const grupos: InvestigatorRecord[][] = [];
	for (let i = 0; i < investigators.length; i += porGrupo) {
		grupos.push(investigators.slice(i, i + porGrupo));
	}
	// Un grupo final incompleto desbalancea la grilla y hace "saltar" la altura al
	// rotar: se descarta, porque la muestra ya es arbitraria.
	return grupos.filter((grupo) => grupo.length === porGrupo);
}

interface FlechaVitrinaProps {
	direccion: 'anterior' | 'siguiente';
	onClick: () => void;
	className?: string;
}

/** Flecha de la vitrina: 32px, mismo borde y hover que las fichas. */
function FlechaVitrina({ direccion, onClick, className = '' }: FlechaVitrinaProps) {
	const Icono = direccion === 'anterior' ? ChevronLeft : ChevronRight;
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={direccion === 'anterior' ? 'Anteriores' : 'Siguientes'}
			className={`flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white text-[#354857] transition hover:border-[#233a4d]/40 hover:text-[#233a4d] ${className}`}
		>
			<Icono size={16} aria-hidden />
		</button>
	);
}

interface HomeInvestigadoresProps {
	investigators: InvestigatorRecord[];
	/** Total del directorio, para el enlace "Ver los N". */
	total: number;
}

/**
 * Vitrina rotativa: muestra 5 perfiles y avanza de 5 en 5 sola.
 *
 * TODOS los grupos viven en el DOM y se mueven con `translateX` dentro de un
 * contenedor con `overflow: hidden`. No es capricho de implementación: es lo que
 * exige la regla de SEO del proyecto —lo que solo se renderiza al interactuar no
 * existe para un rastreador—, así que los 20 perfiles y sus enlaces viajan en el
 * HTML servido aunque solo 5 se vean. Con `display: none` en los grupos
 * inactivos, Google vería cinco.
 *
 * Los grupos ocultos llevan `inert`: siguen en el documento para el rastreador,
 * pero salen del orden de tabulación y de los lectores de pantalla, que si no
 * anunciarían 20 tarjetas de las que solo 5 se ven.
 *
 * La rotación se detiene al pasar el mouse o al enfocar con teclado, y no
 * arranca si el sistema pide menos movimiento.
 *
 * Flechas: a los lados de los puntos, bajo las fichas, en todos los anchos: a
 * los costados del riel le quitaban ancho a las fichas. Dan la vuelta en los
 * extremos.
 */
export function HomeInvestigadores({ investigators, total }: HomeInvestigadoresProps) {
	const esCelular = useIsMobile();
	const grupos = agrupar(investigators, esCelular ? POR_GRUPO_CELULAR : POR_GRUPO_ESCRITORIO);
	const [activo, setActivo] = useState(0);
	const [pausado, setPausado] = useState(false);
	// Se incrementa en cada navegación manual: al estar en las dependencias del
	// efecto, reinicia el intervalo. Sin esto, un clic a los 5,9 s dejaría ver el
	// grupo elegido una décima y la vitrina saltaría sola al siguiente.
	const [reinicio, setReinicio] = useState(0);

	useEffect(() => {
		if (grupos.length < 2 || pausado) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const id = setInterval(() => {
			setActivo((actual) => (actual + 1) % grupos.length);
		}, INTERVALO);

		return () => clearInterval(id);
	}, [grupos.length, pausado, reinicio]);

	if (grupos.length === 0) return null;
	// Al rotar el teléfono cambia el tamaño de grupo y con él la cantidad de
	// grupos, así que el índice activo puede quedar fuera de rango.
	const indiceActivo = Math.min(activo, grupos.length - 1);

	const irA = (indice: number) => {
		setActivo((indice + grupos.length) % grupos.length);
		setReinicio((n) => n + 1);
	};
	const anterior = () => irA(indiceActivo - 1);
	const siguiente = () => irA(indiceActivo + 1);

	// `pt-10` en celular contra las 16 de escritorio: es lo que termina de subir
	// este título al primer pantallazo, que es donde tiene que estar — es la
	// sección por la que se entra al portal.
	return (
		<div className="bg-white px-6 pb-14 pt-10 sm:px-12 sm:py-16">
			<Reveal>
			<div className="flex items-center justify-between gap-4">
				<HomeSectionTitle icon={Users}>
					<span className="sm:hidden">Investigadores</span>
					<span className="hidden sm:inline">Conoce a nuestros investigadores</span>
				</HomeSectionTitle>
				<Link
					href="/investigadores"
					className="inline-flex shrink-0 items-center gap-2 text-[14px] font-semibold text-[#354857] transition hover:underline"
				>
					Ver los {numberFormatter.format(total)}
					<ArrowRight size={16} aria-hidden />
				</Link>
			</div>

			{/* Dos divs y no uno: el de afuera pone la separación, el de adentro
			    recorta. `overflow-hidden` recorta en la caja de PADDING, así que esos
			    12px verticales —compensados con margen negativo para no mover el
			    layout— son el aire que necesita el hover de la ficha, que la levanta
			    2px y antes quedaba cortado por arriba. No se puede fusionar en un
			    solo elemento: `mt-*` y `-my-*` chocan sobre la misma propiedad. */}
			<div className="mt-8">
				<div
					className="-my-3 overflow-hidden py-3"
					onMouseEnter={() => setPausado(true)}
					onMouseLeave={() => setPausado(false)}
					onFocusCapture={() => setPausado(true)}
					onBlurCapture={() => setPausado(false)}
				>
					<div
						className="flex transition-transform duration-700 ease-out motion-reduce:transition-none"
						style={{ transform: `translateX(-${indiceActivo * 100}%)` }}
					>
						{grupos.map((grupo, indice) => (
							<div
								key={grupo[0].slug}
								className="grid w-full shrink-0 grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
								inert={indice !== indiceActivo}
							>
								{grupo.map((inv) => (
									// Misma anatomía que la ficha del directorio
									// (`InvestigadoresDirectoryClient`): radio 10px, borde
									// slate-200, avatar CUADRADO de 72px y el badge de tipo.
									<Link
										key={inv.slug}
										href={`/investigadores/${inv.slug}`}
										className="flex min-h-[156px] flex-col items-center gap-2.5 rounded-[10px] border border-slate-300 bg-white p-[15px] sm:min-h-[198px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-[#233a4d]/40"
									>
										<div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#F5F6FA] text-[#233a4d]">
											<ProfilePhotoImg
												url={inv.fotoPerfilUrl}
												alt={inv.fullName || inv.displayName}
												oculta={inv.fotoPerfilOculta}
												imgClassName="h-full w-full rounded-[10px] object-cover"
												fallback={<UserRound className="h-9 w-9" />}
											/>
										</div>
										<div className="w-full min-w-0 text-center">
											<h3 className="truncate text-[13.5px] font-semibold leading-tight text-slate-800 sm:text-[15px]">
												{inv.displayName}
											</h3>
											{(inv.tipoInvestigador || inv.rolInvestigadorNombre) && (
												<div className="mt-1 flex justify-center">
													<TipoInvestigadorBadge
														tipo={inv.tipoInvestigador}
														nombre={inv.rolInvestigadorNombre}
													/>
												</div>
											)}
											<p
												className="mt-1 line-clamp-2 text-[13px] leading-snug text-slate-600 max-sm:sr-only"
												title={inv.unit}
											>
												{nombreUnidad(inv)}
											</p>
										</div>
									</Link>
								))}
							</div>
						))}
					</div>
				</div>
			</div>

			</Reveal>

			{grupos.length > 1 && (
				<div className="mt-7 flex items-center justify-center gap-2">
					<FlechaVitrina direccion="anterior" onClick={anterior} className="mr-2" />
					{grupos.map((grupo, indice) => (
						<button
							key={grupo[0].slug}
							type="button"
							onClick={() => irA(indice)}
							aria-label={`Ver el grupo ${indice + 1} de ${grupos.length}`}
							aria-current={indice === indiceActivo}
							// El área clicable es de 24px aunque el punto mida 6: un blanco
							// de 6px es imposible de acertar, sobre todo en táctil.
							className="group flex h-6 w-6 cursor-pointer items-center justify-center"
						>
							<span
								className={`h-1.5 rounded-full transition-all duration-300 ${
									indice === indiceActivo
										? 'w-6 bg-[#354857]'
										: 'w-1.5 bg-slate-300 group-hover:bg-slate-400'
								}`}
							/>
						</button>
					))}
					<FlechaVitrina direccion="siguiente" onClick={siguiente} className="ml-2" />
				</div>
			)}
		</div>
	);
}
